import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArchive,
  faBoxArchive,
  faChevronDown,
  faChevronUp,
  faExternalLinkAlt,
  faPen,
  faPlus,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { executeCapability } from '../../../utils/capabilityExecute'
import FleetPhotoReplaceFrame from './FleetPhotoReplaceFrame'
import { compressImageFiles, errorMessage } from '../types'
import type { FleetPanelTab, FleetUnit } from './fleetTypes'
import {
  formatFleetDate,
  formatFleetDateTime,
  formatFleetLocationLabel,
  formatFleetMoney,
  formatHistoryAction,
  formatYearMakeModel,
  getFleetLatLng,
  googleMapsUrlForFleet,
  formatFleetCoords,
} from './fleetTypes'

type MaintenanceDraft = {
  date: string
  mileage: string
  name: string
  description: string
  vendor: string
  price: string
  invoiceImageUrls: string[]
}

const emptyMaintenanceDraft = (): MaintenanceDraft => ({
  date: new Date().toISOString().slice(0, 10),
  mileage: '',
  name: '',
  description: '',
  vendor: '',
  price: '',
  invoiceImageUrls: [],
})

export type FleetMaintenancePayload = {
  date: string
  mileage?: number
  name: string
  description?: string
  vendor?: string
  price?: number
  invoiceImageUrls?: string[]
}

type FleetContactOption = {
  employeeId: string
  name: string
  preferredName?: string | null
  position?: string
}

type FleetDetailPanelProps = {
  unit: FleetUnit
  canManage: boolean
  busy?: boolean
  /** docked = desktop right column; overlay = mobile fullscreen */
  presentation: 'docked' | 'overlay'
  onClose: () => void
  onArchive: (unit: FleetUnit) => void
  onUnarchive: (unit: FleetUnit) => void
  onAddMaintenance: (unit: FleetUnit, payload: FleetMaintenancePayload) => Promise<void>
  onUpdateUnitImages?: (unit: FleetUnit, imageUrls: string[]) => Promise<void>
  onUpdateNotes?: (unit: FleetUnit, notes: string) => Promise<void>
  onUpdateComplianceImages?: (
    unit: FleetUnit,
    kind: 'registration' | 'insurance',
    imageUrls: string[],
  ) => Promise<void>
  onUpdateAssignedEmployee?: (unit: FleetUnit, assignedEmployeeId: string | null) => Promise<void>
}

function InfoRow({
  label,
  value,
  action,
}: {
  label: string
  value?: React.ReactNode
  action?: React.ReactNode
}) {
  const empty =
    value === null ||
    value === undefined ||
    value === '' ||
    value === '—' ||
    (typeof value === 'string' && !value.trim())
  return (
    <div className="flex flex-col gap-0.5 border-b border-base-200 py-2 last:border-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="text-xs font-medium uppercase tracking-wide text-base-content/50 shrink-0">
        {label}
      </dt>
      <dd
        className={`text-sm sm:text-right break-words min-w-0 flex items-start gap-2 sm:justify-end ${
          empty ? 'text-base-content/40 italic' : ''
        }`}
      >
        <span className="min-w-0">{empty ? '—' : value}</span>
        {action ? <span className="shrink-0">{action}</span> : null}
      </dd>
    </div>
  )
}

function flattenLinxup(
  value: unknown,
  prefix = '',
  out: Array<{ key: string; value: string }> = [],
): Array<{ key: string; value: string }> {
  if (value == null) return out
  if (typeof value !== 'object') {
    out.push({ key: prefix || 'value', value: String(value) })
    return out
  }
  if (Array.isArray(value)) {
    value.forEach((item, i) => flattenLinxup(item, `${prefix}[${i}]`, out))
    return out
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const next = prefix ? `${prefix}.${k}` : k
    if (v != null && typeof v === 'object') flattenLinxup(v, next, out)
    else out.push({ key: next, value: v == null ? '' : String(v) })
  }
  return out
}

/** Leaf key names already covered by the primary Info section (or aliases). */
const PRIMARY_INFO_KEY_ALIASES = new Set(
  [
    'name',
    'devicename',
    'nickname',
    'vehiclename',
    'vin',
    'vehiclevin',
    'year',
    'vehicleyear',
    'make',
    'vehiclemake',
    'model',
    'vehiclemodel',
    'status',
    'notes',
    'address',
    'formattedaddress',
    'locationaddress',
    'assignedto',
    'assignedemployeeid',
    'appdrivername',
    'drivername',
    'driver',
    'currentdrivername',
    'personname',
    'firstname',
    'lastname',
    'licenseplate',
    'licenceplate',
    'plate',
    'plateNumber',
    'platenumber',
    'latitude',
    'lat',
    'longitude',
    'lng',
    'lon',
    'latlng',
    'latlong',
    'latlog',
    'coordinates',
    'lastsyncedat',
    'lastseenat',
    'externalsource',
    'externalkey',
  ].map((k) => k.toLowerCase()),
)

function leafKey(path: string): string {
  const parts = path.split('.')
  const last = parts[parts.length - 1] || path
  return last.replace(/\[\d+\]/g, '').toLowerCase()
}

function linxupField(unit: FleetUnit, keys: string[]): string | null {
  const lx = unit.linxup && typeof unit.linxup === 'object' ? (unit.linxup as Record<string, unknown>) : null
  if (!lx) return null
  const want = new Set(keys.map((k) => k.toLowerCase()))
  for (const [key, value] of Object.entries(lx)) {
    if (!want.has(key.toLowerCase())) continue
    if (value == null || typeof value === 'object') continue
    const text = String(value).trim()
    if (text) return text
  }
  return null
}

function fleetLicensePlate(unit: FleetUnit): string | null {
  return linxupField(unit, ['licensePlate', 'licencePlate', 'plate', 'plateNumber', 'license_plate'])
}

function fleetLinxupDriver(unit: FleetUnit): string | null {
  // Same sources as Control Center `getEntityDriverName` / `assetDriverName`.
  return linxupField(unit, ['appDriverName', 'driverName', 'driver', 'currentDriverName'])
}

function fleetAssignedDriver(unit: FleetUnit): string | null {
  const fromLinxup = fleetLinxupDriver(unit)
  if (fromLinxup) return fromLinxup

  const fromUnit = String(unit.assignedTo || '').trim()
  if (!fromUnit) return null
  // Ignore legacy bad sync values that copied the vehicle/truck name into assignedTo.
  const truckName = String(unit.name || '').trim().toLowerCase()
  if (truckName && fromUnit.toLowerCase() === truckName) return null
  return fromUnit
}

function contactDisplayName(contact: Pick<FleetContactOption, 'name' | 'preferredName'>): string {
  const preferred = String(contact.preferredName || '').trim()
  if (preferred) return preferred
  return String(contact.name || '').trim() || '—'
}

function buildLinxupExtraRows(unit: FleetUnit): Array<{ key: string; value: string }> {
  const latLng = getFleetLatLng(unit)
  const licensePlate = fleetLicensePlate(unit)
  const assignedDriver = fleetAssignedDriver(unit)
  const primaryValues = new Set(
    [
      unit.name,
      unit.vin,
      unit.year != null ? String(unit.year) : null,
      unit.make,
      unit.model,
      formatYearMakeModel(unit),
      unit.status,
      unit.notes,
      unit.assignedTo,
      unit.assignedEmployeeId,
      assignedDriver,
      licensePlate,
      unit.location?.address,
      formatFleetLocationLabel(unit),
      latLng ? formatFleetCoords(latLng) : null,
      latLng ? String(latLng.lat) : null,
      latLng ? String(latLng.lng) : null,
      latLng ? latLng.lat.toFixed(5) : null,
      latLng ? latLng.lng.toFixed(5) : null,
      unit.lastSyncedAt ? formatFleetDateTime(unit.lastSyncedAt) : null,
      unit.lastSeenAt ? formatFleetDateTime(unit.lastSeenAt) : null,
      unit.externalSource,
    ]
      .filter((v) => v !== null && v !== undefined && String(v).trim() && String(v) !== '—')
      .map((v) => String(v).trim().toLowerCase()),
  )

  const fromLinxup = flattenLinxup(unit.linxup)
  const sourceRows =
    fromLinxup.length > 0
      ? fromLinxup
      : [
          { key: 'serialNumber', value: unit.externalIdentifiers?.serialNumber || '' },
          { key: 'imei', value: unit.externalIdentifiers?.imei || '' },
          { key: 'deviceUUID', value: unit.externalIdentifiers?.deviceUUID || '' },
          { key: 'trackerId', value: unit.externalIdentifiers?.trackerId || '' },
        ]

  const seenKeys = new Set<string>()
  const seenValues = new Set<string>(primaryValues)
  const extras: Array<{ key: string; value: string }> = []

  for (const row of sourceRows) {
    const value = String(row.value || '').trim()
    if (!value) continue
    const keyNorm = leafKey(row.key)
    if (PRIMARY_INFO_KEY_ALIASES.has(keyNorm)) continue
    if (seenKeys.has(keyNorm)) continue
    const valueNorm = value.toLowerCase()
    if (seenValues.has(valueNorm)) continue
    seenKeys.add(keyNorm)
    seenValues.add(valueNorm)
    extras.push({ key: row.key, value })
  }
  return extras
}

export const FleetDetailPanel: React.FC<FleetDetailPanelProps> = ({
  unit,
  canManage,
  busy = false,
  presentation,
  onClose,
  onArchive,
  onUnarchive,
  onAddMaintenance,
  onUpdateUnitImages,
  onUpdateNotes,
  onUpdateComplianceImages,
  onUpdateAssignedEmployee,
}) => {
  const [tab, setTab] = useState<FleetPanelTab>('info')
  const [galleryIndex, setGalleryIndex] = useState(0)
  const [registrationIndex, setRegistrationIndex] = useState(0)
  const [insuranceIndex, setInsuranceIndex] = useState(0)
  const [invoiceIndex, setInvoiceIndex] = useState(0)
  const [photoBusy, setPhotoBusy] = useState<'unit' | 'registration' | 'insurance' | 'invoice' | null>(
    null,
  )
  const [linxupDetailsOpen, setLinxupDetailsOpen] = useState(false)
  const [editingNotes, setEditingNotes] = useState(false)
  const [notesDraft, setNotesDraft] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState(false)
  const [assignmentQuery, setAssignmentQuery] = useState('')
  const [assignmentContacts, setAssignmentContacts] = useState<FleetContactOption[]>([])
  const [assignmentLoading, setAssignmentLoading] = useState(false)
  const [assignmentError, setAssignmentError] = useState<string | null>(null)
  const [savingAssignment, setSavingAssignment] = useState(false)
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false)
  const [draft, setDraft] = useState<MaintenanceDraft>(emptyMaintenanceDraft)
  const [savingMaintenance, setSavingMaintenance] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    setTab('info')
    setGalleryIndex(0)
    setRegistrationIndex(0)
    setInsuranceIndex(0)
    setInvoiceIndex(0)
    setPhotoBusy(null)
    setLinxupDetailsOpen(false)
    setEditingNotes(false)
    setNotesDraft('')
    setSavingNotes(false)
    setEditingAssignment(false)
    setAssignmentQuery('')
    setAssignmentContacts([])
    setAssignmentLoading(false)
    setAssignmentError(null)
    setSavingAssignment(false)
    setShowMaintenanceForm(false)
    setDraft(emptyMaintenanceDraft())
    setFormError(null)
  }, [unit._id])

  useEffect(() => {
    if (presentation !== 'overlay') return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [presentation])

  const linxupDriver = useMemo(() => fleetLinxupDriver(unit), [unit])
  const manualEmployeeId = String(unit.assignedEmployeeId || '').trim()

  useEffect(() => {
    if (!editingAssignment) return
    let cancelled = false
    setAssignmentLoading(true)
    setAssignmentError(null)
    void (async () => {
      try {
        const data = await executeCapability<FleetContactOption[]>({
          app: 'tally',
          capability: 'fleet.listContacts',
          input: {},
        })
        if (!cancelled) setAssignmentContacts(Array.isArray(data) ? data : [])
      } catch (e) {
        if (!cancelled) {
          setAssignmentContacts([])
          setAssignmentError(errorMessage(e, 'Failed to load contacts'))
        }
      } finally {
        if (!cancelled) setAssignmentLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [editingAssignment])

  const assignmentResults = useMemo(() => {
    const q = assignmentQuery.trim().toLowerCase()
    if (!q) return []
    return assignmentContacts
      .filter((c) => {
        const hay = [c.name, c.preferredName, c.employeeId, c.position]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return hay.includes(q)
      })
      .slice(0, 25)
  }, [assignmentContacts, assignmentQuery])

  const images = useMemo(
    () => (Array.isArray(unit.imageUrls) ? unit.imageUrls.filter(Boolean) : []),
    [unit.imageUrls],
  )
  const registrationImages = useMemo(
    () => (Array.isArray(unit.registrationImageUrls) ? unit.registrationImageUrls.filter(Boolean) : []),
    [unit.registrationImageUrls],
  )
  const insuranceImages = useMemo(
    () => (Array.isArray(unit.insuranceImageUrls) ? unit.insuranceImageUrls.filter(Boolean) : []),
    [unit.insuranceImageUrls],
  )

  const linxupRows = useMemo(() => buildLinxupExtraRows(unit), [unit])

  const maintenanceLogs = useMemo(() => {
    const logs = [...(unit.maintenanceLogs || [])]
    logs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    return logs
  }, [unit.maintenanceLogs])

  const history = useMemo(() => {
    const entries = [...(unit.history || [])]
    entries.sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
    )
    return entries
  }, [unit.history])

  const locationLabel = useMemo(() => formatFleetLocationLabel(unit), [unit])
  const mapsUrl = useMemo(() => googleMapsUrlForFleet(unit), [unit])
  const licensePlate = useMemo(() => fleetLicensePlate(unit), [unit])
  const assignedDriver = useMemo(() => fleetAssignedDriver(unit), [unit])
  const canEditAssignment = canManage && !linxupDriver && Boolean(onUpdateAssignedEmployee)
  const contactName = String(unit.assignedContactName || '').trim()
  const assignedDriverDisplay =
    linxupDriver ||
    (contactName
      ? `${contactName}${manualEmployeeId ? ` (${manualEmployeeId})` : ''}`
      : null) ||
    manualEmployeeId ||
    assignedDriver

  const assignContact = async (employeeId: string) => {
    if (!onUpdateAssignedEmployee) return
    setSavingAssignment(true)
    setAssignmentError(null)
    try {
      await onUpdateAssignedEmployee(unit, employeeId)
      setEditingAssignment(false)
      setAssignmentQuery('')
      setAssignmentContacts([])
    } catch (e) {
      setAssignmentError(errorMessage(e, 'Failed to assign driver'))
    } finally {
      setSavingAssignment(false)
    }
  }

  const clearAssignment = async () => {
    if (!onUpdateAssignedEmployee) return
    setSavingAssignment(true)
    setAssignmentError(null)
    try {
      await onUpdateAssignedEmployee(unit, null)
      setEditingAssignment(false)
      setAssignmentQuery('')
      setAssignmentContacts([])
    } catch (e) {
      setAssignmentError(errorMessage(e, 'Failed to clear assignment'))
    } finally {
      setSavingAssignment(false)
    }
  }

  const submitMaintenance = async () => {
    setFormError(null)
    const name = draft.name.trim()
    if (!name) {
      setFormError('Name is required')
      return
    }
    setSavingMaintenance(true)
    try {
      await onAddMaintenance(unit, {
        date: draft.date || new Date().toISOString(),
        name,
        description: draft.description.trim() || undefined,
        vendor: draft.vendor.trim() || undefined,
        mileage: draft.mileage.trim() ? Number(draft.mileage) : undefined,
        price: draft.price.trim() ? Number(draft.price) : undefined,
        invoiceImageUrls: draft.invoiceImageUrls.length ? draft.invoiceImageUrls : undefined,
      })
      setShowMaintenanceForm(false)
      setDraft(emptyMaintenanceDraft())
      setInvoiceIndex(0)
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save maintenance')
    } finally {
      setSavingMaintenance(false)
    }
  }

  const addUnitImages = async (files: FileList | null) => {
    if (!files?.length || !onUpdateUnitImages) return
    setPhotoBusy('unit')
    try {
      const added = await compressImageFiles(files)
      const next = [...images, ...added]
      await onUpdateUnitImages(unit, next)
      setGalleryIndex(Math.max(0, next.length - 1))
    } finally {
      setPhotoBusy(null)
    }
  }

  const deleteUnitImage = async (idx: number) => {
    if (!onUpdateUnitImages) return
    setPhotoBusy('unit')
    try {
      const next = images.filter((_, i) => i !== idx)
      await onUpdateUnitImages(unit, next)
      setGalleryIndex((prev) => {
        if (!next.length) return 0
        return Math.min(prev, next.length - 1)
      })
    } finally {
      setPhotoBusy(null)
    }
  }

  const addComplianceImages = async (
    kind: 'registration' | 'insurance',
    files: FileList | null,
  ) => {
    if (!files?.length || !onUpdateComplianceImages) return
    setPhotoBusy(kind)
    try {
      const current = kind === 'registration' ? registrationImages : insuranceImages
      const added = await compressImageFiles(files)
      const next = [...current, ...added]
      await onUpdateComplianceImages(unit, kind, next)
      if (kind === 'registration') setRegistrationIndex(Math.max(0, next.length - 1))
      else setInsuranceIndex(Math.max(0, next.length - 1))
    } finally {
      setPhotoBusy(null)
    }
  }

  const deleteComplianceImage = async (kind: 'registration' | 'insurance', idx: number) => {
    if (!onUpdateComplianceImages) return
    setPhotoBusy(kind)
    try {
      const current = kind === 'registration' ? registrationImages : insuranceImages
      const next = current.filter((_, i) => i !== idx)
      await onUpdateComplianceImages(unit, kind, next)
      if (kind === 'registration') {
        setRegistrationIndex((prev) => (next.length ? Math.min(prev, next.length - 1) : 0))
      } else {
        setInsuranceIndex((prev) => (next.length ? Math.min(prev, next.length - 1) : 0))
      }
    } finally {
      setPhotoBusy(null)
    }
  }

  const addInvoiceImages = async (files: FileList | null) => {
    if (!files?.length) return
    setPhotoBusy('invoice')
    try {
      const added = await compressImageFiles(files)
      setDraft((d) => {
        const next = [...d.invoiceImageUrls, ...added]
        setInvoiceIndex(Math.max(0, next.length - 1))
        return { ...d, invoiceImageUrls: next }
      })
    } finally {
      setPhotoBusy(null)
    }
  }

  const deleteInvoiceImage = async (idx: number) => {
    setDraft((d) => {
      const next = d.invoiceImageUrls.filter((_, i) => i !== idx)
      setInvoiceIndex((prev) => (next.length ? Math.min(prev, next.length - 1) : 0))
      return { ...d, invoiceImageUrls: next }
    })
  }

  const startEditNotes = () => {
    setNotesDraft(String(unit.notes || ''))
    setEditingNotes(true)
  }

  const saveNotes = async () => {
    if (!onUpdateNotes) return
    setSavingNotes(true)
    try {
      await onUpdateNotes(unit, notesDraft)
      setEditingNotes(false)
    } finally {
      setSavingNotes(false)
    }
  }

  const panelInner = (
    <>
      <div className="flex shrink-0 items-start gap-3 border-b border-base-200 px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold leading-snug truncate">{unit.name}</h2>
          <p className="text-sm text-base-content/70">{formatYearMakeModel(unit)}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {unit.status ? (
              <span className="badge badge-outline badge-sm capitalize">{unit.status}</span>
            ) : null}
            {unit.vin ? <span className="badge badge-ghost badge-sm font-mono">{unit.vin}</span> : null}
            {unit.externalSource === 'linxup' ? (
              <span className="badge badge-success badge-outline badge-sm">Linxup</span>
            ) : null}
          </div>
        </div>
        <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={onClose} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="shrink-0 px-4 pt-3">
        <FleetPhotoReplaceFrame
          images={images}
          index={galleryIndex}
          onIndexChange={setGalleryIndex}
          alt={unit.name}
          emptyLabel="No photos yet"
          canEdit={canManage && Boolean(onUpdateUnitImages)}
          disabled={busy}
          busy={photoBusy === 'unit'}
          onAdd={(files) => void addUnitImages(files)}
          onDelete={(idx) => deleteUnitImage(idx)}
        />
      </div>

      <div role="tablist" className="tabs tabs-lift mx-4 mt-3 shrink-0">
        {(
          [
            ['info', 'Info'],
            ['compliance', 'Compliance'],
            ['maintenance', 'Maintenance'],
            ['history', 'History'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            className={`tab ${tab === id ? 'tab-active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {tab === 'info' ? (
          <dl>
            <InfoRow label="Name" value={unit.name} />
            <InfoRow label="Year / Make / Model" value={formatYearMakeModel(unit)} />
            <InfoRow label="VIN" value={unit.vin} />
            <InfoRow label="License plate" value={licensePlate} />
            <InfoRow label="Status" value={unit.status} />
            <InfoRow
              label="Assigned driver"
              value={
                editingAssignment ? (
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:items-end">
                    <input
                      type="text"
                      className="input input-bordered input-sm w-full max-w-sm text-left"
                      value={assignmentQuery}
                      onChange={(e) => setAssignmentQuery(e.target.value)}
                      placeholder="Search contacts…"
                      disabled={savingAssignment}
                      autoFocus
                      aria-label="Search contacts to assign"
                    />
                    {assignmentLoading ? (
                      <p className="text-xs text-base-content/50 flex items-center gap-1">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        Loading contacts…
                      </p>
                    ) : null}
                    {assignmentError ? <p className="text-xs text-error">{assignmentError}</p> : null}
                    {assignmentQuery.trim() && !assignmentLoading && assignmentResults.length === 0 && !assignmentError ? (
                      <p className="text-xs text-base-content/50 italic">No contacts found</p>
                    ) : null}
                    {assignmentResults.length > 0 ? (
                      <ul className="menu menu-sm w-full max-w-sm rounded-box border border-base-300 bg-base-100 p-1 max-h-48 overflow-y-auto text-left">
                        {assignmentResults.map((contact) => {
                          const label = contactDisplayName(contact)
                          const empId = String(contact.employeeId || '').trim()
                          return (
                            <li key={empId || label}>
                              <button
                                type="button"
                                className="justify-between gap-2"
                                disabled={savingAssignment || !empId}
                                onClick={() => void assignContact(empId)}
                              >
                                <span className="min-w-0 truncate">{label}</span>
                                <span className="text-xs opacity-60 shrink-0">{empId}</span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                    <div className="flex flex-wrap gap-2 justify-end">
                      {manualEmployeeId ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs text-error"
                          disabled={savingAssignment}
                          onClick={() => void clearAssignment()}
                        >
                          Clear
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={savingAssignment}
                        onClick={() => {
                          setEditingAssignment(false)
                          setAssignmentQuery('')
                          setAssignmentContacts([])
                          setAssignmentError(null)
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  assignedDriverDisplay
                )
              }
              action={
                canEditAssignment && !editingAssignment ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-circle"
                    title="Assign driver"
                    aria-label="Assign driver from contacts"
                    disabled={busy || savingAssignment}
                    onClick={() => {
                      setEditingAssignment(true)
                      setAssignmentQuery('')
                      setAssignmentContacts([])
                      setAssignmentError(null)
                    }}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                ) : null
              }
            />
            <InfoRow
              label="Location"
              value={locationLabel}
              action={
                mapsUrl ? (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost btn-xs btn-circle"
                    title="Open in Google Maps"
                    aria-label="Open location in Google Maps"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </a>
                ) : null
              }
            />
            <InfoRow
              label="Notes"
              value={
                editingNotes ? (
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:items-end">
                    <textarea
                      className="textarea textarea-bordered textarea-sm w-full min-h-20 text-left"
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      disabled={savingNotes}
                      placeholder="Add notes…"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={savingNotes}
                        onClick={() => {
                          setEditingNotes(false)
                          setNotesDraft('')
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-xs"
                        disabled={savingNotes}
                        onClick={() => void saveNotes()}
                      >
                        <FontAwesomeIcon
                          icon={savingNotes ? faSpinner : faPen}
                          className={savingNotes ? 'animate-spin' : ''}
                        />
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  unit.notes
                )
              }
              action={
                canManage && onUpdateNotes && !editingNotes ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-circle"
                    title="Edit notes"
                    aria-label="Edit notes"
                    onClick={startEditNotes}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                ) : null
              }
            />
            <div className="mt-4 mb-2 flex items-center gap-2">
              <button
                type="button"
                className={`badge gap-1.5 h-7 cursor-pointer ${
                  linxupDetailsOpen ? 'badge-primary' : 'badge-ghost'
                }`}
                onClick={() => setLinxupDetailsOpen((open) => !open)}
                aria-expanded={linxupDetailsOpen}
              >
                Linxup details
                {linxupRows.length ? (
                  <span className="opacity-70">{linxupRows.length}</span>
                ) : null}
                <FontAwesomeIcon icon={linxupDetailsOpen ? faChevronUp : faChevronDown} className="text-[10px]" />
              </button>
            </div>
            {linxupDetailsOpen ? (
              <>
                <InfoRow label="Last synced" value={formatFleetDateTime(unit.lastSyncedAt)} />
                {linxupRows.length === 0 ? (
                  <p className="text-sm text-base-content/60 italic">No additional Linxup fields.</p>
                ) : (
                  linxupRows.map((row) => <InfoRow key={row.key} label={row.key} value={row.value} />)
                )}
              </>
            ) : null}
          </dl>
        ) : null}

        {tab === 'compliance' ? (
          <div className="grid grid-cols-2 gap-3">
            <section className="rounded-box border border-base-200 bg-base-200/30 p-3">
              <h4 className="mb-2 font-semibold text-sm">Registration</h4>
              <FleetPhotoReplaceFrame
                images={registrationImages}
                index={registrationIndex}
                onIndexChange={setRegistrationIndex}
                alt={`${unit.name} registration`}
                emptyLabel="No registration photo"
                canEdit={canManage && Boolean(onUpdateComplianceImages)}
                disabled={busy}
                busy={photoBusy === 'registration'}
                onAdd={(files) => void addComplianceImages('registration', files)}
                onDelete={(idx) => deleteComplianceImage('registration', idx)}
              />
            </section>

            <section className="rounded-box border border-base-200 bg-base-200/30 p-3">
              <h4 className="mb-2 font-semibold text-sm">Insurance</h4>
              <FleetPhotoReplaceFrame
                images={insuranceImages}
                index={insuranceIndex}
                onIndexChange={setInsuranceIndex}
                alt={`${unit.name} insurance`}
                emptyLabel="No insurance photo"
                canEdit={canManage && Boolean(onUpdateComplianceImages)}
                disabled={busy}
                busy={photoBusy === 'insurance'}
                onAdd={(files) => void addComplianceImages('insurance', files)}
                onDelete={(idx) => deleteComplianceImage('insurance', idx)}
              />
            </section>
          </div>
        ) : null}

        {tab === 'maintenance' ? (
          <div className="flex flex-col gap-3">
            {canManage ? (
              showMaintenanceForm ? (
                <div className="rounded-box border border-base-300 bg-base-200/30 p-3 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-base-content/70">Date</span>
                      <input
                        type="date"
                        className="input input-bordered input-sm w-full"
                        value={draft.date}
                        onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-base-content/70">Mileage</span>
                      <input
                        type="number"
                        className="input input-bordered input-sm w-full"
                        value={draft.mileage}
                        onChange={(e) => setDraft((d) => ({ ...d, mileage: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-base-content/70">Name</span>
                    <input
                      type="text"
                      className="input input-bordered input-sm w-full"
                      value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                      placeholder="Oil change, brakes…"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-base-content/70">Description</span>
                    <textarea
                      className="textarea textarea-bordered textarea-sm w-full"
                      rows={2}
                      value={draft.description}
                      onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-base-content/70">Vendor</span>
                      <input
                        type="text"
                        className="input input-bordered input-sm w-full"
                        value={draft.vendor}
                        onChange={(e) => setDraft((d) => ({ ...d, vendor: e.target.value }))}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-base-content/70">Price</span>
                      <input
                        type="number"
                        step="0.01"
                        className="input input-bordered input-sm w-full"
                        value={draft.price}
                        onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-base-content/70">Invoice</span>
                    <FleetPhotoReplaceFrame
                      images={draft.invoiceImageUrls}
                      index={invoiceIndex}
                      onIndexChange={setInvoiceIndex}
                      alt="Maintenance invoice"
                      emptyLabel="No invoice photo"
                      canEdit
                      disabled={savingMaintenance}
                      busy={photoBusy === 'invoice'}
                      onAdd={(files) => void addInvoiceImages(files)}
                      onDelete={(idx) => deleteInvoiceImage(idx)}
                    />
                  </div>
                  {formError ? <p className="text-sm text-error">{formError}</p> : null}
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={savingMaintenance}
                      onClick={() => {
                        setShowMaintenanceForm(false)
                        setFormError(null)
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={savingMaintenance}
                      onClick={() => void submitMaintenance()}
                    >
                      <FontAwesomeIcon
                        icon={savingMaintenance ? faSpinner : faPlus}
                        className={savingMaintenance ? 'animate-spin' : ''}
                      />
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary btn-sm self-start"
                  onClick={() => setShowMaintenanceForm(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  New maintenance
                </button>
              )
            ) : null}

            {maintenanceLogs.length === 0 ? (
              <p className="py-8 text-center text-sm text-base-content/60 italic">
                No maintenance logs yet
              </p>
            ) : (
              <ul className="space-y-2">
                {maintenanceLogs.map((log) => (
                  <li
                    key={log._id || `${log.date}-${log.name}`}
                    className="rounded-box border border-base-200 bg-base-100 p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium">{log.name}</p>
                        <p className="text-xs text-base-content/60 mt-0.5">
                          {formatFleetDate(log.date)}
                          {log.mileage != null ? ` · ${Number(log.mileage).toLocaleString()} mi` : ''}
                        </p>
                      </div>
                      <span className="badge badge-ghost badge-sm shrink-0">
                        {formatFleetMoney(log.price)}
                      </span>
                    </div>
                    {log.description ? (
                      <p className="mt-2 text-sm text-base-content/80">{log.description}</p>
                    ) : null}
                    {Array.isArray(log.invoiceImageUrls) && log.invoiceImageUrls.some(Boolean) ? (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-base-content/60 mb-1">Invoice</p>
                        <div className="flex flex-wrap gap-2">
                          {log.invoiceImageUrls.filter(Boolean).map((url, idx) => (
                            <a
                              key={`${log._id || log.name}-invoice-${idx}`}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block h-24 w-32 overflow-hidden rounded-box border border-base-300 bg-base-200"
                              title="Open invoice photo"
                            >
                              <img src={url} alt={`Invoice ${idx + 1}`} className="h-full w-full object-contain" />
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-base-content/60">
                      {log.vendor ? <span>Vendor: {log.vendor}</span> : null}
                      {log.createdBy ? <span>Logged by {log.createdBy}</span> : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {tab === 'history' ? (
          history.length === 0 ? (
            <p className="py-8 text-center text-sm text-base-content/60 italic">No history yet</p>
          ) : (
            <ul className="timeline timeline-vertical timeline-compact">
              {history.map((entry, idx) => (
                <li key={entry._id || `${entry.action}-${entry.createdAt}-${idx}`}>
                  {idx > 0 ? <hr /> : null}
                  <div className="timeline-start text-xs text-base-content/50 whitespace-nowrap">
                    {formatFleetDateTime(entry.createdAt)}
                  </div>
                  <div className="timeline-middle">
                    <span className="badge badge-primary badge-xs" />
                  </div>
                  <div className="timeline-end timeline-box text-sm mb-3">
                    <div className="font-medium capitalize">{formatHistoryAction(entry.action)}</div>
                    {entry.note ? (
                      <div className="text-xs mt-1 text-base-content/70">{entry.note}</div>
                    ) : null}
                    {entry.userName ? (
                      <div className="text-xs text-base-content/60 mt-1">by {entry.userName}</div>
                    ) : null}
                  </div>
                  {idx < history.length - 1 ? <hr /> : null}
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>

      {canManage ? (
        <div className="shrink-0 border-t border-base-200 px-4 py-3 flex justify-end gap-2">
          {unit.status === 'archived' ? (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              disabled={busy}
              onClick={() => onUnarchive(unit)}
            >
              <FontAwesomeIcon icon={busy ? faSpinner : faBoxArchive} className={busy ? 'animate-spin' : ''} />
              Unarchive
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-outline btn-error btn-sm"
              disabled={busy}
              onClick={() => onArchive(unit)}
            >
              <FontAwesomeIcon icon={busy ? faSpinner : faArchive} className={busy ? 'animate-spin' : ''} />
              Archive
            </button>
          )}
        </div>
      ) : null}
    </>
  )

  if (presentation === 'docked') {
    return (
      <aside
        className="hidden md:flex h-full min-h-0 w-full max-w-md shrink-0 flex-col border-l border-base-300 bg-base-100 lg:max-w-lg"
        role="complementary"
        aria-label={`${unit.name} details`}
      >
        {panelInner}
      </aside>
    )
  }

  return createPortal(
    <div className="fixed inset-0 z-[999] flex md:hidden">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close fleet panel"
        onClick={onClose}
      />
      <aside
        className="relative ml-auto flex h-[100dvh] w-full flex-col bg-base-100 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={`${unit.name} details`}
      >
        {panelInner}
      </aside>
    </div>,
    document.body,
  )
}

export default FleetDetailPanel
